class TokenDistributionWebSocket {
  constructor() {
    this.ws = null;
    this.externalURL = 'https://token-ai-agent-d09010489214.herokuapp.com';
    this.callbacks = new Map();
    this.taskCallbacks = new Map();
    this.currentTaskId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
  }

  async connectToTask(taskId) {
    // Close existing connection if any
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.currentTaskId = taskId;

    return new Promise((resolve, reject) => {
      try {
        // Construct WebSocket URL for specific task - direct to external API
        const wsBase = this.externalURL.replace('https://', 'wss://').replace('http://', 'ws://');
        const wsURL = `${wsBase}/ws/token_transfer/${taskId}`;
        
        console.log('Connecting to token transfer WebSocket:', wsURL);
        this.ws = new WebSocket(wsURL);

        this.ws.onopen = () => {
          console.log('Token transfer WebSocket connected for task:', taskId);
          this.reconnectAttempts = 0;
          resolve(this.ws);
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('Token transfer WebSocket message:', data);

            // Enhance data with task_id and type
            const enhancedData = {
              ...data,
              task_id: taskId,
              type: this.determineMessageType(data)
            };

            // Call task-specific callback if exists
            if (this.taskCallbacks.has(taskId)) {
              const callback = this.taskCallbacks.get(taskId);
              callback(enhancedData);
            }

            // Notify all general callbacks
            this.callbacks.forEach(callback => {
              try {
                callback(enhancedData);
              } catch (error) {
                console.error('Error in WebSocket callback:', error);
              }
            });

            // Disconnect when finished
            if (data.finished === true) {
              console.log('Transfer finished, closing WebSocket');
              setTimeout(() => {
                if (this.ws) {
                  this.ws.close();
                }
              }, 100);
            }
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('Token transfer WebSocket closed:', event.code, event.reason);
          this.ws = null;
          
          // Clean up task callback if transfer is complete
          if (this.currentTaskId && this.taskCallbacks.has(this.currentTaskId)) {
            this.taskCallbacks.delete(this.currentTaskId);
          }
          
          this.currentTaskId = null;

          // Notify callbacks about disconnection
          this.callbacks.forEach(callback => {
            callback({ type: 'disconnected', code: event.code, reason: event.reason });
          });
        };

        this.ws.onerror = (error) => {
          console.error('Token transfer WebSocket error:', error);
          reject(error);
        };

        // Connection timeout
        setTimeout(() => {
          if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
            this.ws.close();
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000);
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        reject(error);
      }
    });
  }

  determineMessageType(data) {
    // Determine message type based on data content
    if (data.finished === true) {
      if (data.failed > 0) {
        return 'transfer_error';
      }
      return 'transfer_complete';
    }
    return 'transfer_progress';
  }

  // Subscribe to messages for a specific task
  subscribeToTask(taskId, callback) {
    this.taskCallbacks.set(taskId, callback);
  }

  // Unsubscribe from a specific task
  unsubscribeFromTask(taskId) {
    this.taskCallbacks.delete(taskId);
  }

  // General message subscription
  subscribe(id, callback) {
    this.callbacks.set(id, callback);
  }

  unsubscribe(id) {
    this.callbacks.delete(id);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }
    this.callbacks.clear();
    this.taskCallbacks.clear();
    this.currentTaskId = null;
    this.reconnectAttempts = 0;
  }
}

// Create singleton instance
const tokenDistributionWS = new TokenDistributionWebSocket();

export default tokenDistributionWS;